import type { RuleCode } from "./rule-codes.generated";

/**
 * The rule catalog entry whose user line says why a page's capture from its
 * link failed, by the capture's typed reason (python-api
 * `survey/media/capture.py` reason codes, and the Save's own
 * `capture_returned_no_image` / `enrichment_transport_failed`). The one owner
 * of that line: the link dialog and the composer say it when a capture fails
 * (`friendlyCaptureError`), readiness raises it on a saved page that has no
 * capture (`assetEnrichmentAdvisories`), and the chat reply reads it in the
 * page's facts (`turn-reply.ts`, `notCaptured`). Wave E L-CAP: LP-26's pages
 * were too tall and LP-24's came back empty; the chat never said so and the
 * editor showed a generic note, the real reason only in the link dialog.
 */
const PAGE_CAPTURE_LINE_BY_REASON: Readonly<Record<string, RuleCode>> = {
  full_page_too_tall: "page_capture_too_large",
  capture_too_large: "page_capture_too_large",
  full_page_changed: "page_capture_changed",
  http_error: "page_capture_http_error",
  bot_wall: "page_capture_bot_wall",
  empty: "page_capture_empty",
  timeout: "page_capture_timeout",
  nav_timeout: "page_capture_timeout",
  unreachable: "page_capture_unreachable",
  ssrf_blocked: "page_capture_url_not_allowed",
  invalid_url: "page_capture_invalid_url",
};

/**
 * The catalog code for a capture's failure reason. A reason with no line of
 * its own (capture's `error` and `capture_dimensions_invalid`, the Save's
 * transport failures, a reason a newer capture adds) is the catalog's
 * `page_capture_failed`, as the link dialog said "Could not capture this
 * page" for every other reason.
 */
export function pageCaptureLineCode(reason: unknown): RuleCode {
  return (
    (typeof reason === "string" && Object.prototype.hasOwnProperty.call(PAGE_CAPTURE_LINE_BY_REASON, reason)
      ? PAGE_CAPTURE_LINE_BY_REASON[reason]
      : undefined) ?? "page_capture_failed"
  );
}
